import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import {
  Flame,
  Brain,
  HeartPulse,
  Sparkles,
  BriefcaseBusiness,
  Users,
  GraduationCap,
  BookOpen,
  Star,
  FileText,
  BadgeCheck,
  Target,
  Rocket,
  CalendarClock,
  CreditCard,
  BarChart3,
  MonitorSmartphone,
  Clapperboard,
  ShieldCheck,
  Headset
} from 'lucide-react';

// Import SEO content
import {
  trustSection,
  howItWorks,
  expandedFAQ,
  featuresSection,
  ctaSection,
} from './seoContent';

import RegisterModal from './RegisterModal';

const SignUp = () => {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const navigate = useNavigate();

  const trustIcons: Record<string, { icon: any; iconClass: string; cardClass: string }> = {
    'Active Students': {
      icon: Users,
      iconClass: 'text-[#3F6652]',
      cardClass: 'bg-[#3F6652]/10 ring-1 ring-[#3F6652]/20',
    },
    'Verified Instructors': {
      icon: GraduationCap,
      iconClass: 'text-[#E6A93F]',
      cardClass: 'bg-[#E6A93F]/10 ring-1 ring-[#E6A93F]/20',
    },
    'Classes': {
      icon: BookOpen,
      iconClass: 'text-[#B9576B]',
      cardClass: 'bg-[#B9576B]/10 ring-1 ring-[#B9576B]/20',
    },
    'Average Rating': {
      icon: Star,
      iconClass: 'text-[#57628F]',
      cardClass: 'bg-[#57628F]/10 ring-1 ring-[#57628F]/20',
    },
  };

  const howItWorksIcons: Record<string, { icon: any; iconClass: string; cardClass: string }> = {
    'Register Your Profile': {
      icon: FileText,
      iconClass: 'text-[#3F6652]',
      cardClass: 'bg-[#3F6652]/10 ring-1 ring-[#3F6652]/20',
    },
    'Get Verified': {
      icon: BadgeCheck,
      iconClass: 'text-[#E6A93F]',
      cardClass: 'bg-[#E6A93F]/10 ring-1 ring-[#E6A93F]/20',
    },
    'Create Your Classes': {
      icon: Target,
      iconClass: 'text-[#B9576B]',
      cardClass: 'bg-[#B9576B]/10 ring-1 ring-[#B9576B]/20',
    },
    'Start Teaching': {
      icon: Rocket,
      iconClass: 'text-[#57628F]',
      cardClass: 'bg-[#57628F]/10 ring-1 ring-[#57628F]/20',
    },
  };

  const featureIcons: Record<string, { icon: any; iconClass: string; cardClass: string }> = {
    'Smart Scheduling': {
      icon: CalendarClock,
      iconClass: 'text-[#3F6652]',
      cardClass: 'bg-[#3F6652]/10 ring-1 ring-[#3F6652]/20',
    },
    'Secure Payments': {
      icon: CreditCard,
      iconClass: 'text-[#E6A93F]',
      cardClass: 'bg-[#E6A93F]/10 ring-1 ring-[#E6A93F]/20',
    },
    'Analytics Dashboard': {
      icon: BarChart3,
      iconClass: 'text-[#B9576B]',
      cardClass: 'bg-[#B9576B]/10 ring-1 ring-[#B9576B]/20',
    },
    'Mobile App': {
      icon: MonitorSmartphone,
      iconClass: 'text-[#57628F]',
      cardClass: 'bg-[#57628F]/10 ring-1 ring-[#57628F]/20',
    },
    'Video Streaming': {
      icon: Clapperboard,
      iconClass: 'text-[#3F6652]',
      cardClass: 'bg-[#3F6652]/10 ring-1 ring-[#3F6652]/20',
    },
    'Student Management': {
      icon: Users,
      iconClass: 'text-[#E6A93F]',
      cardClass: 'bg-[#E6A93F]/10 ring-1 ring-[#E6A93F]/20',
    },
    'Data Security': {
      icon: ShieldCheck,
      iconClass: 'text-[#B9576B]',
      cardClass: 'bg-[#B9576B]/10 ring-1 ring-[#B9576B]/20',
    },
    '24/7 Support': {
      icon: Headset,
      iconClass: 'text-[#57628F]',
      cardClass: 'bg-[#57628F]/10 ring-1 ring-[#57628F]/20',
    },
  };

  return (
    <div className="min-h-screen bg-[#F5F0E3] text-[#15302B] font-sans selection:bg-[#E6A93F] selection:text-[#15302B]">
      <Toaster position="top-right" />

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-10 pb-36 px-4 sm:px-6 lg:px-8 border-b border-[#ECE4CF]/60">
        {/* Decorative Blur Blobs */}
        <div className="absolute inset-0 pointer-events-none opacity-20">
          <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#E6A93F]/20 blur-3xl animate-pulse" />
          <div className="absolute top-20 right-20 w-80 h-80 rounded-full bg-[#3F6652]/20 blur-3xl animate-bounce" style={{ animationDuration: '12s' }} />
        </div>

        <div className="relative max-w-5xl mx-auto text-center z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#ECE4CF] bg-[#FFFDF7]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#3F6652] mb-6">
            <Sparkles className="h-3.5 w-3.5 text-[#E6A93F] animate-pulse" />
            Trusted by wellness hosts across India
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold text-[#15302B] tracking-tight mb-6 leading-tight">
            Become a Global Partner with <span className="text-[#3F6652] italic font-serif">Metafit</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#3B5750] max-w-3xl mx-auto mb-10 leading-relaxed">
            Scale your classes, consultations, wellness retreats, and therapies across India and beyond.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
            <button
              onClick={() => window.location.href = 'https://partner.metafitwellness.com/'}
              className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-[#15302B] bg-[#FFFDF7] hover:bg-[#ECE4CF] border border-[#15302B]/10 rounded-full shadow-md transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              LOGIN ACCOUNT
            </button>
            <button
              onClick={() => {
                setIsRegisterModalOpen(true);
              }}
              className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-[#15302B] bg-[#E6A93F] hover:bg-[#C98726] rounded-full shadow-lg shadow-[#E6A93F]/20 transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              REGISTER AS PARTNER
            </button>
          </div>
        </div>
      </div>

      {/* Category Cards Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="group rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-[#E6A93F]/30 hover:shadow-md">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E6A93F]/10 border border-[#E6A93F]/20 text-[#E6A93F] group-hover:scale-110 transition-transform">
              <Flame className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-[#15302B] group-hover:text-[#E6A93F] transition-colors">Yoga & Fitness</h3>
            <p className="text-sm leading-relaxed text-[#3B5750]">Connect with students and teach yoga, meditation, personal training, and physical wellness classes.</p>
          </div>

          <div className="group rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-[#3F6652]/30 hover:shadow-md">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#3F6652]/10 border border-[#3F6652]/20 text-[#3F6652] group-hover:scale-110 transition-transform">
              <HeartPulse className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-[#15302B] group-hover:text-[#3F6652] transition-colors">Ayurveda & Health</h3>
            <p className="text-sm leading-relaxed text-[#3B5750]">Offer certified consultations, medical guidelines, diet plans, and traditional Ayurvedic advices.</p>
          </div>

          <div className="group rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-[#B9576B]/30 hover:shadow-md">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#B9576B]/10 border border-[#B9576B]/20 text-[#B9576B] group-hover:scale-110 transition-transform">
              <Brain className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-[#15302B] group-hover:text-[#B9576B] transition-colors">Mental Wellness</h3>
            <p className="text-sm leading-relaxed text-[#3B5750]">Help clients overcome stress, anxiety, cognitive issues, and enhance general mental health.</p>
          </div>

          <div className="group rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-[#57628F]/30 hover:shadow-md">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#57628F]/10 border border-[#57628F]/20 text-[#57628F] group-hover:scale-110 transition-transform">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-[#15302B] group-hover:text-[#57628F] transition-colors">Retreats & Therapies</h3>
            <p className="text-sm leading-relaxed text-[#3B5750]">Organize premium treatment retreats, relaxing sound therapy sessions, and holistic treatments.</p>
          </div>
        </div>
      </div>

      {/* Trust & Stats Section */}
      <div className="bg-[#FFFDF7] py-24 px-4 sm:px-6 lg:px-8 border-b border-[#ECE4CF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#15302B] mb-4 font-serif">{trustSection.title}</h2>
            <p className="text-[#3B5750] text-lg max-w-2xl mx-auto">{trustSection.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustSection.stats.map((stat: any, index: number) => {
              const trustIcon = trustIcons[stat.label];
              const TrustIcon = trustIcon?.icon || BriefcaseBusiness;

              return (
                <div key={index} className="rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-8 text-center shadow-sm transition-all duration-300 hover:border-[#15302B]/20 hover:shadow-md">
                  <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${trustIcon?.cardClass || 'bg-[#ECE4CF]/50 border border-[#ECE4CF]'} text-[#3F6652]`}>
                    <TrustIcon className={`h-8 w-8 ${trustIcon?.iconClass || 'text-[#3F6652]'}`} strokeWidth={2} />
                  </div>
                  <div className="mb-2 text-4xl font-black text-[#15302B]">{stat.number}</div>
                  <div className="mb-2 font-bold text-[#3B5750]">{stat.label}</div>
                  <p className="text-sm leading-relaxed text-[#3B5750]/80">{stat.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-[#F5F0E3] py-24 px-4 sm:px-6 lg:px-8 border-b border-[#ECE4CF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#15302B] mb-4 font-serif">{howItWorks.title}</h2>
            <p className="text-[#3B5750] text-lg max-w-2xl mx-auto">{howItWorks.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.steps.map((step: any, index: number) => {
              const stepIcon = howItWorksIcons[step.title];
              const StepIcon = stepIcon?.icon || BriefcaseBusiness;

              return (
                <div key={index} className="relative rounded-3xl border border-[#ECE4CF] bg-[#FFFDF7] p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${stepIcon?.cardClass || 'bg-[#ECE4CF]/50 border border-[#ECE4CF]'} text-[#3F6652]`}>
                    <StepIcon className={`h-8 w-8 ${stepIcon?.iconClass || 'text-[#3F6652]'}`} strokeWidth={2} />
                  </div>
                  <div className="mb-2 text-2xl font-black text-[#3F6652]">{step.number}</div>
                  <h3 className="mb-3 font-bold text-[#15302B] text-lg">{step.title}</h3>
                  <p className="mb-5 text-sm leading-relaxed text-[#3B5750]">{step.description}</p>
                  <span className="inline-flex rounded-full bg-[#E6A93F]/20 px-3 py-1 text-xs font-semibold text-[#15302B]">{step.time}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-[#FFFDF7] py-24 px-4 sm:px-6 lg:px-8 border-b border-[#ECE4CF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#15302B] mb-4 font-serif">{featuresSection.title}</h2>
            <p className="text-[#3B5750] text-lg max-w-2xl mx-auto">{featuresSection.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuresSection.features.map((feature: any, index: number) => {
              const featureIcon = featureIcons[feature.title];
              const FeatureIcon = featureIcon?.icon || BriefcaseBusiness;

              return (
                <div key={index} className="rounded-3xl border border-[#ECE4CF] bg-[#F5F0E3]/50 p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${featureIcon?.cardClass || 'bg-[#ECE4CF]/50 border border-[#ECE4CF]'} text-[#3F6652]`}>
                    <FeatureIcon className={`h-6 w-6 ${featureIcon?.iconClass || 'text-[#3F6652]'}`} strokeWidth={2} />
                  </div>
                  <h3 className="mb-2 font-bold text-[#15302B] text-lg">{feature.title}</h3>
                  <p className="text-sm leading-relaxed text-[#3B5750]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-[#F5F0E3] py-24 px-4 sm:px-6 lg:px-8 border-b border-[#ECE4CF]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#15302B] mb-16 text-center font-serif">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {expandedFAQ.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="space-y-4">
                <h3 className="text-xl font-bold text-[#3F6652] mb-4 border-l-4 border-[#3F6652] pl-3">{section.category}</h3>
                <div className="space-y-3">
                  {section.faqs.map((faq: any, faqIndex: number) => (
                    <details key={faqIndex} className="group border border-[#ECE4CF] rounded-2xl bg-[#FFFDF7] p-5 hover:border-[#3F6652]/30 hover:shadow-md transition duration-300">
                      <summary className="cursor-pointer font-bold text-[#15302B] flex justify-between items-center list-none outline-none">
                        <span className="pr-4">{faq.question}</span>
                        <span className="text-[#3F6652] group-open:rotate-180 transition-transform duration-300">▼</span>
                      </summary>
                      <p className="text-[#3B5750] mt-4 text-sm leading-relaxed">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#2C4A3B] via-[#3F6652] to-[#E6A93F] py-24 px-4 sm:px-6 lg:px-8 text-[#FFFDF7] border-t border-[#ECE4CF]">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_#FFFDF7_1px,_transparent_1px)] bg-[size:24px_24px]" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-6 leading-tight font-serif">{ctaSection.title}</h2>
          <p className="text-xl text-[#FFFDF7]/90 mb-4 font-semibold">{ctaSection.subtitle}</p>
          <p className="text-[#FFFDF7]/80 max-w-2xl mx-auto mb-10 leading-relaxed">{ctaSection.description}</p>

          <button
            onClick={() => {
              setIsRegisterModalOpen(true);
            }}
            className="px-10 py-4 text-base font-bold text-[#15302B] bg-[#E6A93F] hover:bg-[#C98726] rounded-full shadow-xl shadow-[#15302B]/20 transition-all duration-300 transform hover:scale-[1.03]"
          >
            REGISTER NOW
          </button>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t border-[#FFFDF7]/20 text-[#FFFDF7]/80">
            {ctaSection.trustBadges.map((badge: string, index: number) => (
              <div key={index} className="text-xs sm:text-sm flex items-center justify-center gap-1.5 font-medium">
                {badge}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partner Registration Modal */}
      <RegisterModal isOpen={isRegisterModalOpen} onClose={() => setIsRegisterModalOpen(false)} />
    </div>
  );
};

export default SignUp;
